// Generated by ts-to-zod
import { z } from 'zod';

const formatTypeSchema = z.union([
  z.literal('VIDEO'),
  z.literal('IMAGE'),
  z.literal('DOCUMENT'),
  z.literal('TEXT'),
  z.literal('AUDIO'),
]);

export const parameterTypeSchema = z.enum([
  'image',
  'text',
  'payload',
  'document',
  'audio',
  'video',
  'cards',
]);

const normalizedParameterTypeSchema = z
  .string()
  .transform((val) => val.toLowerCase())
  .pipe(parameterTypeSchema);

const exampleSchema = z.object({
  header_handle: z.array(z.string()).optional(),
  body_text: z.array(z.array(z.string())).optional(),
});

const fileSchema = z.object({
  link: z.string(),
  filename: z.string().optional(),
  caption: z.string().optional(),
});

export const parameterSchema = z.object({
  text: z.coerce.string().optional(),
  type: normalizedParameterTypeSchema,
  payload: z.string().optional(),
  image: fileSchema.optional(),
  document: fileSchema.optional(),
  video: fileSchema.optional(),
  audio: fileSchema.optional(),
});

const buttonSchema = z.object({
  url: z.string().optional(),
  text: z.string(),
  type: z.string(),
  example: z.array(z.string()).optional(),
  parameters: z.array(parameterSchema).optional(),
});

const cardSchema = z.object({
  card_index: z.number().optional(),
  components: z.array(
    z.object({
      type: z.string(),
      example: exampleSchema.optional(),
      format: formatTypeSchema.optional(),
      text: z.string().optional(),
      parameters: z.array(parameterSchema).optional(),
      buttons: z.array(buttonSchema).optional(),
    })
  ),
});

export const componentSchema = z.object({
  type: z.string(),
  example: exampleSchema.optional(),
  format: formatTypeSchema.optional(),
  text: z.string().optional(),
  buttons: z.array(buttonSchema).optional(),
  cards: z.array(cardSchema).optional(),
});

const paramsSchema = z.object({
  type: z.string(),
  parameters: z.array(parameterSchema).optional(),
  buttons: z.array(buttonSchema).optional(),
  index: z.string().optional(),
  sub_type: z.string().optional(),
});

export const templateParams = z.array(paramsSchema);

export const templateComponents = z.array(componentSchema);

const templateCardSchema = z.object({
  index: z.string(),
  components: cardSchema.shape.components,
  params: templateParams,
  buttons: z.array(buttonSchema).optional(),
});

export const templateSchema = z.object({
  params: templateParams,
  components: templateComponents,
  cards: z.array(templateCardSchema).optional(),
});

export const templateSchemaV2 = z.object({
  components: z.array(
    z.object({
      type: z.string(),
      parameters: z.array(parameterSchema).optional(),
      buttons: z.array(buttonSchema).optional(),
    })
  ),
  name: z.string(),
  language: z.object({
    code: z.string(),
  }),
});

export const templateSchemaV3 = z.object({
  category: z.string().optional(),
  components: templateComponents,
  id: z.string().optional(),
  language: z.string().optional(),
  name: z.string(),
  status: z.string().optional(),
  waba_id: z.string().optional(),
  isCustomized: z.boolean().optional(),
  quality_score: z
    .object({
      date: z.number().optional(),
      score: z.string().optional(),
    })
    .optional(),
  rejected_reason: z.string().optional(),
});

export type TemplateParameters = z.infer<typeof parameterSchema>;

export type TemplateParams = z.infer<typeof templateParams>;

export type TemplateComponent = z.infer<typeof componentSchema>;

export type TemplateCard = z.infer<typeof templateCardSchema>;

export type TemplateButton = z.infer<typeof buttonSchema>;

export const transformSchemaV2toV1 = templateSchemaV2.transform((template) => {
  const { name, components } = template;

  const updatedComponents = components.map(({ type, parameters, buttons }) => {
    const textParameters =
      parameters?.filter(({ type }) => type === 'text') || [];
    const imageParameters =
      parameters?.filter(({ type }) => type === 'image') || [];
    const docParameters =
      parameters?.filter(({ type }) => type === 'document') || [];

    return {
      type: type.toUpperCase(),
      buttons,
      text: textParameters.map((_, index) => `{{${index + 1}}}`).join(' '),
      format:
        imageParameters?.length > 0
          ? 'IMAGE'
          : docParameters?.length > 0
            ? 'DOCUMENT'
            : 'TEXT',
    };
  });

  const params: TemplateParams = components.map(
    ({ type, parameters = [] }) => ({
      type,
      parameters,
    })
  );

  return { [name]: { components: updatedComponents, params } };
});

export const transformSchemaV3toV1 = templateSchemaV3.transform((template) => {
  const { name, components } = template;

  const cards: TemplateCard[] = [];

  const params: TemplateParams = components.map(
    ({ type, example, format, text, cards: templateCards = [] }) => {
      if (type === 'CAROUSEL' && templateCards.length > 0) {
        templateCards.forEach((card, index) => {
          const cardParams: TemplateParams = [];
          const cardComponents: TemplateComponent[] = [];
          const cardButtons: TemplateButton[] = [];
          card.components.forEach((cardComponent) => {
            cardComponents.push(cardComponent);
            const { parameters, buttons, ...restComponent } = cardComponent;
            if (buttons) cardButtons.push(...(buttons || []));
            else
              cardParams.push({
                type: restComponent.type,
                parameters: parameters || [],
              });
          });
          cards.push({
            index: String(index),
            components: cardComponents,
            params: cardParams,
            buttons: cardButtons,
          });
        });
        return { type, cards: [] };
      }
      const parameters: TemplateParameters[] = [];
      if (format === 'IMAGE') {
        parameters.push({
          type: 'image',
          image: { link: example?.header_handle?.[0] || '' },
        });
      } else if (format === 'VIDEO') {
        parameters.push({
          type: 'video',
          video: { link: example?.header_handle?.[0] || '' },
        });
      } else if (format === 'DOCUMENT') {
        parameters.push({
          type: 'document',
          document: { link: example?.header_handle?.[0] || '' },
        });
      } else if (text) {
        example?.body_text?.[0]?.forEach((text: string) =>
          parameters.push({
            type: 'text',
            text,
          })
        );
      }
      return { type, parameters };
    }
  );

  return {
    [name]: {
      components: components.filter((x) => x.type.toUpperCase() !== 'CAROUSEL'),
      params: params.filter((x) => x.type.toUpperCase() !== 'CAROUSEL'),
      cards,
    },
  };
});

export const noIntentTemplateSchema = z.record(
  z.string(),
  z.object({
    url: z.string(),
    caption: z.string(),
    filename: z.string().optional(),
  })
);

export const messageTemplateSchema = z.record(z.string(), templateSchema);

const sendComponentsSchema = z.array(
  z.object({
    type: z.string(),
    parameters: z.array(
      z.object({
        type: z.string(),
        text: z.string().optional(),
        image: fileSchema.optional(),
        buttons: z.array(buttonSchema).optional(),
      })
    ),
  })
);

export const sendMsgTemplateSchema = z.object({
  components: sendComponentsSchema,
  language: z.object({ code: z.string().optional() }).optional(),
  name: z.string(),
});

export const mappedTemplateSchema = z.array(
  z.object({
    type: z.string(),
    format: formatTypeSchema.optional(),
    text: z.string().optional(),
    parameters: z.array(parameterSchema),
  })
);
